import React, { useEffect, useRef, useState } from 'react'

import moment from 'config/moment'
import { useApi } from 'hooks'
import { ERRORS } from 'consts'
import { snackbar } from 'components'

import StoresAndDateFilter from '../StoresAndDateFilter/StoresAndDateFilter'
import { getStores } from '../StoresAndDateFilter/api'
import { GetStoresResponse, Store } from '../StoresAndDateFilter/types'
import { useDashboardFilters } from '../contexts/DashboardFiltersContext'
import { TicketsByAppGeneral } from '../OverviewTab/TicketsByAppGeneral/TicketsByAppGeneral'
import TicketsByAppByStore from '../OverviewTab/TicketsByAppByStore/TicketsByAppByStore'
import { UploadTickets } from '../OverviewTab/TicketsByAppGeneral/UploadTickets'

const TicketsByAppTab = () => {
  const {
    filters,
    handlePeriodChange,
    handleStoreChange,
    handleCompareStoreChange,
    handleComparaChange,
    handleApply,
    isActiveTab,
  } = useDashboardFilters()
  const { period, store, compareStore, compara, applyTrigger, isLoading } = filters

  const getStoresResponseGetter = (responseData: GetStoresResponse) => responseData?.stores ?? []
  const [{ data: stores }] = useApi<GetStoresResponse, Store[]>(
    getStores,
    getStoresResponseGetter,
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )

  const getStoreId = (storeName: string): number | undefined => {
    if (storeName === 'all') return undefined
    const storeItem = stores.find(s => s.name === storeName)
    return storeItem?.id
  }
  const storeId = getStoreId(store)

  // Convert period (month/year) to date ranges for ticket components
  const period1StartDate = moment()
    .year(period.year)
    .month(period.month)
    .startOf('month')
  const period1EndDate = moment()
    .year(period.year)
    .month(period.month)
    .endOf('month')

  // Period 2 is the previous month for comparison
  const period2StartDate = period1StartDate.clone().subtract(1, 'month')
  const period2EndDate = period1EndDate.clone().subtract(1, 'month')

  // Convert storeId to storeIds array if needed
  const storeIds = storeId ? [storeId] : undefined

  // Only trigger API calls if this tab is active
  const shouldTrigger = isActiveTab('tickets-by-app')

  // Ejecutar indicadores secuencialmente: uno termina -> arranca el siguiente
  const [step, setStep] = useState(0)
  const runRef = useRef(0)

  useEffect(() => {
    if (!shouldTrigger) return
    runRef.current = applyTrigger
    setStep(0)
  }, [applyTrigger, shouldTrigger])

  return (
    <div className="flex flex-col gap-8 p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-row items-end gap-4 w-full">
        <StoresAndDateFilter
          period={period}
          onPeriodChange={handlePeriodChange}
          storesList={stores}
          store={store}
          onStoreChange={handleStoreChange}
          compara={compara}
          onComparaChange={handleComparaChange}
          compareStore={compareStore}
          onCompareStoreChange={handleCompareStoreChange}
          onApply={handleApply}
          isLoading={isLoading}
        />
        <UploadTickets />
      </div>
      <TicketsByAppGeneral
        period1={{ startDate: period1StartDate, endDate: period1EndDate }}
        period2={{ startDate: period2StartDate, endDate: period2EndDate }}
        compara={compara}
        trigger={shouldTrigger && step === 0 ? applyTrigger : 0}
        storeIds={storeIds}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 0 ? 1 : prev))
        }}
      />
      <TicketsByAppByStore
        period1={{ startDate: period1StartDate, endDate: period1EndDate }}
        period2={{ startDate: period2StartDate, endDate: period2EndDate }}
        compara={compara}
        trigger={shouldTrigger && step === 1 ? applyTrigger : 0}
        storeIds={storeIds}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 1 ? 2 : prev))
        }}
      />
    </div>
  )
}

export default TicketsByAppTab
